/**
 * Helpers for the "New WhatsApp Chat" feature.
 *
 * Deliberately thin: contact search/create, contactable-inbox merging, and
 * WhatsApp send-payload building already exist in `composeConversationHelper.js`
 * (the same helper module the general "Compose new conversation" popover
 * uses) and are re-exported here rather than copied, so the two features
 * share one implementation. Only logic that is genuinely new for this
 * feature - finding a contact's WhatsApp inbox, and validating a phone
 * number before it's used to search - lives in this file.
 */
import { parsePhoneNumber } from 'libphonenumber-js';
import {
  createContactSearcher,
  createNewContact,
  fetchContactableInboxes,
  mergeInboxDetails,
  prepareWhatsAppMessagePayload,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

export {
  createContactSearcher,
  createNewContact,
  fetchContactableInboxes,
  mergeInboxDetails,
  prepareWhatsAppMessagePayload,
};

const WHATSAPP_CHANNEL_TYPE = 'Channel::Whatsapp';

/**
 * True if the given list of inbox records (the raw, snake_case shape
 * returned by the `inboxes/getInboxes` Vuex getter) contains at least one
 * WhatsApp inbox. Used to decide whether the floating launcher renders at
 * all - accounts with no WhatsApp inbox don't need the feature.
 * @param {Array<Object>} inboxes
 * @returns {boolean}
 */
export const hasWhatsAppInbox = (inboxes = []) =>
  inboxes.some(inbox => inbox.channel_type === WHATSAPP_CHANNEL_TYPE);

/**
 * Picks the first WhatsApp-capable inbox out of a contact's merged
 * contactable-inboxes list (already camelCased by `mergeInboxDetails`).
 * @param {Array<Object>} contactInboxes
 * @returns {Object|null} the WhatsApp inbox, or null if the contact has none
 */
export const findWhatsAppInbox = (contactInboxes = []) =>
  contactInboxes.find(inbox => inbox.channelType === WHATSAPP_CHANNEL_TYPE) ||
  null;

/**
 * Validates a phone number emitted by `PhoneNumberInput`.
 *
 * `PhoneNumberInput` runs its own internal Vuelidate rules but does not
 * expose them to the parent (no `defineExpose`), so callers need an
 * independent way to know whether the current value is a real, dialable
 * number before allowing the flow to move on to contact search.
 * @param {string} value E.164-formatted phone number (e.g. "+14155552671")
 * @returns {boolean}
 */
export const isValidWhatsAppPhoneNumber = value => {
  if (!value) return false;
  try {
    return parsePhoneNumber(value).isValid();
  } catch (error) {
    return false;
  }
};
